'use client'

import { useState, useEffect } from 'react'
import { Users, Mail, Building2, FileText, Trash2, X, Eye, Calendar, Key } from 'lucide-react'
import { panel, panelInner, headingClass, mutedClass, ghostBtn, primaryBtn, primaryBtnInline, fieldClass, labelClass, errorBox, successBox, modalPanel } from './ui'

interface User {
  id: string
  username: string
  email: string
  companyName: string | null
  companyProfile: {
    companyName: string
    industry: string
  } | null
  briefCount: number
  createdAt: string
}

interface Brief {
  id: string
  objective: string | null
  status: string
  createdAt: string
}

interface UserDetail {
  id: string
  username: string
  email: string
  companyName: string | null
  companyProfile: {
    companyName: string
    industry: string
    website: string | null
    description: string | null
  } | null
  briefs: Brief[]
  createdAt: string
}

interface UsersListProps {
  userId: string
}

export default function UsersList({ userId }: UsersListProps) {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [userDetail, setUserDetail] = useState<UserDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [resettingPassword, setResettingPassword] = useState(false)
  const [passwordMessage, setPasswordMessage] = useState<'success' | 'error' | null>(null)

  useEffect(() => {
    loadUsers()
  }, [userId])

  const loadUsers = async () => {
    try {
      const response = await fetch('/api/brief613/users', {
        headers: {
          'x-user-id': userId,
          'x-user-role': 'admin',
        },
      })

      const data = await response.json()

      if (data.success) {
        setUsers(data.users)
      }
    } catch (error) {
      console.error('Error loading users:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleViewDetails = async (user: User) => {
    setSelectedUserId(user.id)
    setLoadingDetail(true)
    try {
      const response = await fetch(`/api/brief613/users/${user.id}`, {
        headers: {
          'x-user-id': userId,
          'x-user-role': 'admin',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON')
      }

      const data = await response.json()

      if (data.success) {
        setUserDetail(data.user)
        setNewPassword('')
        setPasswordMessage(null)
      } else {
        alert(data.error || 'Failed to load client details')
        setSelectedUserId(null)
      }
    } catch (error: any) {
      console.error('Error loading user details:', error)
      alert(`Failed to load client details: ${error.message || 'Unknown error'}`)
      setSelectedUserId(null)
    } finally {
      setLoadingDetail(false)
    }
  }

  const handleDelete = async (userToDelete: User) => {
    const confirmMessage = `⚠️ Are you sure you want to delete client "${userToDelete.username}"?\n\nThis will permanently delete:\n- The user account\n- All their briefs (${userToDelete.briefCount})\n- Their company profile\n- All associated notifications\n\nThis action cannot be undone!`
    
    if (!confirm(confirmMessage)) {
      return
    }

    setDeletingUserId(userToDelete.id)

    try {
      const response = await fetch(`/api/brief613/users/${userToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'x-user-id': userId,
          'x-user-role': 'admin',
        },
      })

      const data = await response.json()

      if (data.success) {
        // Remove user from list
        setUsers(users.filter((u) => u.id !== userToDelete.id))
        alert(`✅ Client "${userToDelete.username}" deleted successfully!\n\nDeleted:\n- ${data.deleted.briefs} briefs\n- ${data.deleted.notifications} notifications`)
      } else {
        alert('❌ Error: ' + (data.error || 'Failed to delete client'))
      }
    } catch (error: any) {
      console.error('Error deleting user:', error)
      alert('❌ Error: ' + (error.message || 'Failed to delete client'))
    } finally {
      setDeletingUserId(null)
    }
  }

  const handleResetPassword = async () => {
    if (!selectedUserId || !newPassword.trim()) return
    setResettingPassword(true)
    setPasswordMessage(null)
    try {
      const response = await fetch(`/api/brief613/users/${selectedUserId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': 'admin',
        },
        body: JSON.stringify({ password: newPassword.trim() }),
      })
      const data = await response.json()
      if (data.success) {
        setPasswordMessage('success')
        setNewPassword('')
      } else {
        setPasswordMessage('error')
      }
    } catch {
      setPasswordMessage('error')
    } finally {
      setResettingPassword(false)
    }
  }

  if (loading) {
    return (
      <div className={`${panel} p-8 text-center`}>
        <div className="w-12 h-12 border-4 border-white/20 border-t-white/80 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/55">Loading users...</p>
      </div>
    )
  }

  return (
    <div className={`${panel} p-6`}>
      <h3 className="text-base font-semibold text-white/90 mb-6">All Clients ({users.length})</h3>

      {users.length === 0 ? (
        <div className="text-center py-12">
          <Users className="mx-auto mb-4 text-white/30" size={32} />
          <p className="text-white/70">No clients yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {users.map((user) => (
            <div 
              key={user.id} 
              className={`${panelInner} p-4 border-l-2 border-l-white/20 cursor-pointer hover:bg-white/5 transition-colors`}
              onClick={(e) => {
                // Don't trigger if clicking on delete button
                if ((e.target as HTMLElement).closest('button')) {
                  return
                }
                handleViewDetails(user)
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <Users size={20} className="text-white/50" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white/90">{user.username}</h4>
                      <p className="text-sm text-white/60 flex items-center gap-1">
                        <Mail size={14} className="text-white/50" />
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <div className="ml-13 flex items-center gap-4 text-sm text-white/70">
                    {user.companyProfile ? (
                      <>
                        <span className="flex items-center gap-1">
                          <Building2 size={14} className="text-white/50" />
                          {user.companyProfile.companyName}
                        </span>
                        <span>• {user.companyProfile.industry}</span>
                      </>
                    ) : user.companyName ? (
                      <span className="flex items-center gap-1">
                        <Building2 size={14} className="text-white/50" />
                        {user.companyName}
                      </span>
                    ) : null}
                    <span className="flex items-center gap-1">
                      <FileText size={14} className="text-white/50" />
                      {user.briefCount} brief{user.briefCount !== 1 ? 's' : ''}
                    </span>
                    <span className="text-white/50">
                      Joined {new Date(user.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleViewDetails(user)
                    }}
                    className="p-2 text-blue-400/60 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                    title={`View details for "${user.username}"`}
                  >
                    <Eye size={18} className="text-white/50" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(user)
                    }}
                    disabled={deletingUserId === user.id}
                    className="p-2 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={`Delete client "${user.username}" and all associated data`}
                  >
                    {deletingUserId === user.id ? (
                      <div className="w-5 h-5 border-2 border-red-400/60 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Trash2 size={18} className="text-white/50" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Client Detail Modal */}
      {selectedUserId && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setSelectedUserId(null)}>
          <div className={`${modalPanel} p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
            {loadingDetail ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-white/20 border-t-white/80 rounded-full animate-spin mx-auto mb-4" />
                <p className="text-white/55">Loading client details...</p>
              </div>
            ) : userDetail ? (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-base font-semibold text-white/90">Client Details</h3>
                  <button
                    onClick={() => setSelectedUserId(null)}
                    className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X size={20} className="text-white/50" />
                  </button>
                </div>

                {/* Client Info */}
                <div className={`${panel} p-6`}>
                  <h4 className="text-sm font-semibold text-white/90 mb-4">Client Information</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Users size={20} className="text-white/50" />
                      <div>
                        <p className="text-white/90 font-semibold">{userDetail.username}</p>
                        <p className="text-sm text-white/60 flex items-center gap-1">
                          <Mail size={14} className="text-white/50" />
                          {userDetail.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-white/70">
                      <Calendar size={14} className="text-white/50" />
                      <span>Joined {new Date(userDetail.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                {/* Reset password */}
                <div className={`${panel} p-6`}>
                  <h4 className="text-sm font-semibold text-white/90 mb-4 flex items-center gap-2">
                    <Key size={20} className="text-white/50" />
                    Reset client password
                  </h4>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-sm text-white/70 mb-1">New password</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        className={fieldClass}
                        disabled={resettingPassword}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleResetPassword}
                      disabled={!newPassword.trim() || resettingPassword}
                      className={primaryBtnInline}
                    >
                      {resettingPassword ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Key size={16} className="text-white/50" />
                      )}
                      Set password
                    </button>
                  </div>
                  {passwordMessage === 'success' && (
                    <p className="mt-2 text-sm text-green-400">Password updated successfully.</p>
                  )}
                  {passwordMessage === 'error' && (
                    <p className="mt-2 text-sm text-red-400">Failed to update password.</p>
                  )}
                </div>

                {/* Company Profile */}
                {userDetail.companyProfile && (
                  <div className={`${panel} p-6`}>
                    <h4 className="text-sm font-semibold text-white/90 mb-4">Company Profile</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Building2 size={16} className="text-white/50" />
                        <span className="text-white/90 font-semibold">{userDetail.companyProfile.companyName}</span>
                      </div>
                      <p className="text-white/70 text-sm">Industry: {userDetail.companyProfile.industry}</p>
                      {userDetail.companyProfile.website && (
                        <p className="text-white/70 text-sm">
                          Website: <a href={userDetail.companyProfile.website} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{userDetail.companyProfile.website}</a>
                        </p>
                      )}
                      {userDetail.companyProfile.description && (
                        <p className="text-white/70 text-sm mt-2">{userDetail.companyProfile.description}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Briefs */}
                <div className={`${panel} p-6`}>
                  <h4 className="text-sm font-semibold text-white/90 mb-4">
                    Briefs ({userDetail.briefs?.length || 0})
                  </h4>
                  {userDetail.briefs && userDetail.briefs.length > 0 ? (
                    <div className="space-y-3">
                      {userDetail.briefs.map((brief) => (
                        <div key={brief.id} className="p-3 bg-white/5 rounded-lg border border-white/10">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-white/90 font-semibold">{brief.objective || 'Untitled Brief'}</p>
                              <p className="text-sm text-white/60 mt-1">
                                Created {new Date(brief.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                            <span
                              className={`px-3 py-1 text-xs font-semibold rounded-full border ${
                                brief.status === 'submitted'
                                  ? 'bg-blue-500/10 text-blue-300/90 border-blue-500/20'
                                  : brief.status === 'in_progress'
                                  ? 'bg-yellow-500/10 text-yellow-300/90 border-yellow-500/20'
                                  : brief.status === 'completed'
                                  ? 'bg-emerald-500/10 text-emerald-300/90 border-emerald-500/20'
                                  : 'bg-white/5 text-white/50 border-white/10'
                              }`}
                            >
                              {brief.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-white/60 text-center py-8">No briefs yet</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-white/70">Failed to load client details</p>
                <button
                  onClick={() => setSelectedUserId(null)}
                  className={ghostBtn}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

